import { NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ZagIcon } from '../icon/icon';

/** Decorative colour tone of a stat's icon container. */
export type StatItemColor = 'brand' | 'success' | 'warning' | 'error' | 'info';

/**
 * Individual stat item configuration.
 *
 * Supports two display modes:
 * 1. **Simple mode** (with icon): Shows icon + label + value, used for dashboard KPI cards
 * 2. **List mode** (with percentage): Shows label + value + progress bar + meta text
 */
export interface StatItem {
  /** Stable identifier — used by consumers to map a clicked tile back to domain data. */
  id?: string;

  /** Display label for the stat */
  label: string;

  /** Display value (pre-formatted string) */
  value: string;

  /** Small unit suffix after the value in simple mode, e.g. "h" */
  unit?: string;

  /**
   * Material icon name for simple mode.
   * When provided, displays the stat as an icon card.
   */
  icon?: string;

  /** Icon container tone; only used with `icon`. @default 'brand' */
  color?: StatItemColor;

  /**
   * Trend percentage for simple mode.
   * Positive = up arrow (green), Negative = down arrow (red)
   */
  trend?: number;

  /**
   * Trend comparison label (e.g., "vs last week")
   * @default 'vs last week'
   */
  trendLabel?: string;

  /**
   * Progress bar percentage (0-100) for list mode.
   * When provided, displays a progress bar below the value.
   */
  percentage?: number;

  /**
   * Additional meta text displayed below the bar in list mode.
   */
  meta?: string;

  /**
   * Progress bar color variant for list mode.
   * @default 'success'
   */
  variant?: 'success' | 'warning' | 'error' | 'info';

  /**
   * Router link for simple-mode cards that should navigate somewhere (e.g. a
   * KPI tile linking to its drill-down page). When set, the card renders as
   * a link instead of a static card.
   */
  route?: string[];

  /** CTA label rendered as link footer on clickable cards. Hidden when unset. */
  cta?: string;
}

/**
 * Display mode for the stats widget
 */
export type StatsWidgetMode = 'auto' | 'simple' | 'list';

/** Stat tiles as icon cards or progress-bar rows; `mode` auto-detects from the first item. */
@Component({
  selector: 'zag-stats-widget',
  imports: [ZagIcon, RouterLink, NgTemplateOutlet],
  template: `
    <div
      class="zag-stats-widget__container"
      [class.zag-stats-widget__container--simple]="isSimpleMode()"
      [class.zag-stats-widget__container--list]="!isSimpleMode()"
    >
      @for (stat of stats(); track stat.label) {
        @if (isSimpleItem(stat)) {
          @let valueFirst = compact() && isSimpleMode();
          <ng-template #cardContent>
            <div class="zag-stats-widget__icon-container" [class]="getIconContainerClass(stat)">
              <span [zagIcon]="stat.icon!" class="zag-stats-widget__icon"></span>
            </div>
            <div class="zag-stats-widget__card-content">
              <ng-template #cardLabel>
                <div class="zag-stats-widget__card-label">{{ stat.label }}</div>
              </ng-template>
              @if (!valueFirst) {
                <ng-container [ngTemplateOutlet]="cardLabel"></ng-container>
              }
              <div class="zag-stats-widget__card-value">
                <span>{{ stat.value }}</span>
                @if (stat.unit) {
                  <span class="zag-stats-widget__card-unit">{{ stat.unit }}</span>
                }
              </div>
              @if (valueFirst) {
                <ng-container [ngTemplateOutlet]="cardLabel"></ng-container>
              }
              @if (stat.trend !== undefined && stat.trend !== null) {
                <div class="zag-stats-widget__trend" [class]="getTrendClass(stat.trend)">
                  <span [zagIcon]="'trending_up'" class="zag-stats-widget__trend-icon"></span>
                  {{ formatTrend(stat.trend, stat.trendLabel) }}
                </div>
              }
              @if (stat.cta && (stat.route || clickable())) {
                <div class="zag-stats-widget__card-cta">
                  {{ stat.cta }}
                  <span zagIcon="arrow_forward"></span>
                </div>
              }
            </div>
          </ng-template>
          @if (stat.route) {
            <a class="zag-stats-widget__card zag-stats-widget__card--link" [routerLink]="stat.route">
              <ng-container [ngTemplateOutlet]="cardContent"></ng-container>
            </a>
          } @else if (clickable()) {
            <div
              class="zag-stats-widget__card zag-stats-widget__card--link"
              role="button"
              tabindex="0"
              [class.zag-stats-widget__card--selected]="isSelected(stat)"
              [attr.aria-pressed]="isSelected(stat)"
              (click)="onTileClick(stat)"
              (keydown)="onTileKeydown($event, stat)"
            >
              <ng-container [ngTemplateOutlet]="cardContent"></ng-container>
            </div>
          } @else {
            <div class="zag-stats-widget__card">
              <ng-container [ngTemplateOutlet]="cardContent"></ng-container>
            </div>
          }
        } @else {
          <!-- List Mode: Progress Bar -->
          <div class="zag-stats-widget__item">
            <div class="zag-stats-widget__label">{{ stat.label }}</div>
            <div class="zag-stats-widget__value">{{ stat.value }}</div>
            @if (stat.percentage !== undefined) {
              <div class="zag-stats-widget__bar">
                <div class="zag-stats-widget__bar-fill" [class]="getBarFillClass(stat)" [style.width.%]="stat.percentage"></div>
              </div>
            }
            @if (stat.meta) {
              <div class="zag-stats-widget__meta">{{ stat.meta }}</div>
            }
          </div>
        }
      }
    </div>
  `,
  styleUrl: './stats-widget.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-stats-widget]': 'true',
    '[class.zag-stats-widget--compact]': 'compact()',
    '[class.zag-stats-widget--kpi]': 'kpi()',
    '[class.zag-stats-widget--columns]': 'kpiColumns() !== undefined',
    '[style.--zag-stats-widget-columns]': 'kpiColumns()',
    '[style.--zag-stats-widget-columns-narrow]': 'kpiColumnsNarrow()',
  },
})
export class StatsWidget {
  /**
   * Array of statistics to display.
   */
  readonly stats = input<StatItem[]>([]);

  /**
   * Display mode for the widget.
   * - `auto`: Automatically detect based on first item's properties (default)
   * - `simple`: Force icon card display
   * - `list`: Force progress bar display
   */
  readonly mode = input<StatsWidgetMode>('auto');

  /** Fixed column count for `kpi` tiles (two below 900px widget width); ignored without `kpi`, auto-fit otherwise. */
  readonly columns = input<number>();

  /** Simple-mode cards without a `route` become role-button cards that emit `tileClick`. */
  readonly clickable = input(false, { transform: booleanAttribute });

  readonly selectedIds = input<readonly string[]>([]);
  readonly compact = input(false, { transform: booleanAttribute });

  /** Bordered KPI tiles: label-first uppercase caption, large figure, tone tile sized `--avatar-size-md`. */
  readonly kpi = input(false, { transform: booleanAttribute });

  readonly tileClick = output<StatItem>();

  protected readonly kpiColumns = computed(() => (this.kpi() ? this.columns() : undefined));

  protected readonly kpiColumnsNarrow = computed(() => {
    const columns = this.kpiColumns();
    return columns === undefined ? undefined : Math.min(columns, 2);
  });

  /**
   * Computed: Whether to use simple mode (icon cards)
   */
  protected readonly isSimpleMode = computed(() => {
    const m = this.mode();
    if (m === 'simple') return true;
    if (m === 'list') return false;

    // Auto-detect: check if first item has an icon
    const items = this.stats();
    return items.length > 0 && !!items[0].icon;
  });

  /**
   * Check if a stat item should render as simple (icon card) mode
   */
  protected isSimpleItem(stat: StatItem): boolean {
    return !!stat.icon;
  }

  /**
   * Get CSS class for icon container based on color
   */
  protected getIconContainerClass(stat: StatItem): string {
    const color = stat.color ?? 'brand';
    return `zag-stats-widget__icon-container--${color}`;
  }

  /**
   * Get CSS class for trend indicator
   */
  protected getTrendClass(trend: number): string {
    return trend >= 0 ? 'zag-stats-widget__trend--positive' : 'zag-stats-widget__trend--negative';
  }

  /**
   * Format trend display text
   */
  protected formatTrend(trend: number, label?: string): string {
    const absValue = Math.abs(trend);
    return `${absValue}% ${label ?? 'vs last week'}`;
  }

  /**
   * Get CSS class for progress bar fill based on variant
   */
  protected getBarFillClass(stat: StatItem): string {
    const variant = stat.variant ?? 'success';
    return `zag-stats-widget__bar-fill--${variant}`;
  }

  protected isSelected(stat: StatItem): boolean {
    if (stat.id === undefined) return false;
    return this.selectedIds().includes(stat.id);
  }

  protected onTileClick(stat: StatItem): void {
    this.tileClick.emit(stat);
  }

  protected onTileKeydown(event: KeyboardEvent, stat: StatItem): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.onTileClick(stat);
  }
}
